import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface Register {
  id: string;
  location_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  device_id?: string | null;
  // Joined fields
  location_name?: string;
  device?: {
    id: string;
    device_id: string;
    device_name: string | null;
    device_type: 'electron' | 'web' | 'mobile';
    is_active: boolean;
    last_seen_at: string | null;
  } | null;
  current_session?: {
    id: string;
    user_id: string;
    user_name: string;
    opened_at: string;
    status: 'open' | 'paused';
    paused_at?: string | null;
  } | null;
}

export function useRegisters(locationId?: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all registers (optionally filtered by location)
  const {
    data: registers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['registers', locationId],
    queryFn: async () => {
      let query = supabase
        .from('registers')
        .select(`
          *,
          location:locations(name),
          device:registered_devices!registers_device_id_fkey(id, device_id, device_name, device_type, is_active, last_seen_at)
        `)
        .order('name');

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching registers:', error);
        throw error;
      }

      // Fetch open sessions for each register
      const registerIds = data?.map((r) => r.id) || [];

      // Only query sessions if we have registers
      let sessionMap = new Map();
      if (registerIds.length > 0) {
        const { data: sessions, error: sessionsError } = await supabase
          .from('cash_drawer_sessions')
          .select(`
            id,
            register_id,
            user_id,
            opened_at,
            status,
            paused_at,
            user:profiles!cash_drawer_sessions_user_id_fkey(first_name, last_name)
          `)
          .in('register_id', registerIds)
          .in('status', ['open', 'paused']); // Include both open and paused sessions

        if (sessionsError) {
          console.error('Error fetching sessions:', sessionsError);
        }

        // Map sessions to registers
        if (sessions && sessions.length > 0) {
          sessionMap = new Map(
            sessions.map((s: any) => [
              s.register_id,
              {
                id: s.id,
                user_id: s.user_id,
                user_name: s.user ? `${s.user.first_name} ${s.user.last_name}` : 'Unknown',
                opened_at: s.opened_at,
                status: s.status,
                paused_at: s.paused_at,
              },
            ])
          );
        }
      }

      return (data || []).map((r: any) => ({
        ...r,
        location_name: r.location?.name || 'Unknown',
        device: r.device || null,
        current_session: sessionMap.get(r.id) || null,
      })) as Register[];
    },
    enabled: !!user,
  });

  // Create register mutation
  const createRegister = useMutation({
    mutationFn: async ({
      locationId,
      name,
    }: {
      locationId: string;
      name: string;
    }) => {
      const { data, error } = await supabase
        .from('registers')
        .insert({
          location_id: locationId,
          name,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registers'] });
      queryClient.invalidateQueries({ queryKey: ['available-registers'] });
    },
  });

  // Update register mutation
  const updateRegister = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<Register, 'name' | 'is_active'>>;
    }) => {
      const { data, error } = await supabase
        .from('registers')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registers'] });
      queryClient.invalidateQueries({ queryKey: ['available-registers'] });
    },
  });

  // Resume a paused session (for managers)
  const resumePausedSession = useMutation({
    mutationFn: async (sessionId: string) => {
      // End break log with manager's ID
      const { error: breakError } = await supabase.rpc('end_break', {
        p_session_id: sessionId,
        p_resumed_by: user?.id,
      });

      if (breakError) {
        console.error('Failed to log break end:', breakError);
        // Continue anyway - logging failure shouldn't prevent resume
      }

      const { error } = await supabase
        .from('cash_drawer_sessions')
        .update({
          status: 'open',
          paused_at: null,
        })
        .eq('id', sessionId)
        .eq('status', 'paused'); // Only resume if currently paused

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registers'] });
      queryClient.invalidateQueries({ queryKey: ['available-registers'] });
      queryClient.invalidateQueries({ queryKey: ['register-session'] });
    },
  });

  // Delete register mutation
  const deleteRegister = useMutation({
    mutationFn: async (id: string) => {
      // Check if register has any sessions (open or closed)
      const { data: sessions } = await supabase
        .from('cash_drawer_sessions')
        .select('id')
        .eq('register_id', id)
        .limit(1);

      if (sessions && sessions.length > 0) {
        throw new Error('Cannot delete register with existing sessions. Deactivate it instead.');
      }

      const { error } = await supabase.from('registers').delete().eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registers'] });
      queryClient.invalidateQueries({ queryKey: ['available-registers'] });
    },
  });

  return {
    registers: registers || [],
    isLoading,
    error,
    createRegister,
    updateRegister,
    deleteRegister,
    resumePausedSession,
  };
}
